/**
 * SquiggleMascot Component
 * 
 * Renders the Squiggle character using PNG sprites with CSS animations.
 * This serves as the primary mascot for the game-like UI redesign.
 */

import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './SquiggleMascot.module.scss';

// Mood types matching the design system
export type SquiggleMood = 
  | 'idle' 
  | 'happy' 
  | 'winking' 
  | 'thinking' 
  | 'encouraging' 
  | 'celebrating';

// Size variants
export type SquiggleSize = 'sm' | 'md' | 'lg';

// Sprite mapping for each mood
const SPRITE_MAP: Record<SquiggleMood, string> = {
  idle: '/squiggle/Private & Shared/Squiggle/My_Minu_Squiggle_yellow-01.png',
  happy: '/squiggle/Private & Shared/Squiggle/My_Minu_Squiggle_yellow-02.png',
  winking: '/squiggle/Private & Shared/Squiggle/My_Minu_Squiggle_yellow-01.png',
  thinking: '/squiggle/Private & Shared/Squiggle/My_Minu_Squiggle_yellow-03.png',
  encouraging: '/squiggle/Private & Shared/Squiggle/My_Minu_Squiggle_yellow-04.png',
  celebrating: '/squiggle/Private & Shared/Squiggle/My_Minu_Squiggle_yellow-05.png',
};

// Size values in pixels
const SIZE_MAP: Record<SquiggleSize, number> = {
  sm: 80,
  md: 120,
  lg: 160,
};

interface SquiggleMascotProps {
  mood?: SquiggleMood;
  size?: SquiggleSize;
  isTalking?: boolean;
  className?: string;
  onClick?: () => void;
  animate?: boolean;
}

export const SquiggleMascot: React.FC<SquiggleMascotProps> = ({
  mood = 'idle',
  size = 'md',
  isTalking = false,
  className = '',
  onClick,
  animate = true,
}) => {
  const spriteUrl = useMemo(() => SPRITE_MAP[mood], [mood]);
  const sizeValue = SIZE_MAP[size];

  // Animation variants based on mood
  const getAnimationVariants = () => {
    if (!animate) {
      return {
        initial: {},
        animate: {},
      };
    }

    switch (mood) {
      case 'celebrating':
        return {
          initial: { scale: 1, rotate: 0 },
          animate: {
            scale: [1, 1.1, 1],
            rotate: [0, -5, 5, 0],
            transition: {
              duration: 0.6,
              repeat: Infinity,
              repeatType: 'loop' as const,
            },
          },
        };
      case 'happy':
        return {
          initial: { scale: 1, y: 0 },
          animate: {
            scale: [1, 1.05, 1],
            y: [0, -5, 0],
            transition: {
              duration: 1.5,
              repeat: Infinity,
              repeatType: 'loop' as const,
              ease: 'easeInOut' as const,
            },
          },
        };
      case 'thinking':
        return {
          initial: { rotate: 0 },
          animate: {
            rotate: [0, 3, 0, -3, 0],
            transition: {
              duration: 2,
              repeat: Infinity,
              repeatType: 'loop' as const,
            },
          },
        };
      case 'winking':
      case 'encouraging':
        return {
          initial: { scale: 1 },
          animate: {
            scale: [1, 1.02, 1],
            transition: {
              duration: 2,
              repeat: Infinity,
              repeatType: 'loop' as const,
            },
          },
        };
      default: // idle
        return {
          initial: { y: 0 },
          animate: {
            y: [0, -8, 0],
            transition: {
              duration: 3,
              repeat: Infinity,
              repeatType: 'loop' as const,
              ease: 'easeInOut' as const,
            },
          },
        };
    }
  };

  const variants = getAnimationVariants();

  return (
    <motion.div
      className={`${styles.mascotContainer} ${className}`}
      style={{ width: sizeValue, height: sizeValue }}
      initial={variants.initial}
      animate={variants.animate}
      onClick={onClick}
      role="img"
      aria-label={`Squiggle mascot feeling ${mood}`}
    >
      <AnimatePresence mode="wait">
        <motion.img
          key={mood}
          src={spriteUrl}
          alt=""
          className={`${styles.sprite} ${isTalking ? styles.talking : ''}`}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.2 }}
          draggable={false}
        />
      </AnimatePresence>
    </motion.div>
  );
};

export default SquiggleMascot;
